// Idle guard for the notification websocket in non-production builds.
//
// Why: every open websocket keeps a Cloud Run instance of the notification
// socket service "active" and billed at full vCPU rate. A single dev tab left
// open (even in the background) holds the socket forever — see the September
// 2026 cost review. Production is exempt: investors expect live notifications
// for as long as the app is open.
//
// The production check fails closed (see utils/deployEnvironment.js): only a
// positively recognised non-production build arms this guard, so a build that
// loses VUE_APP_DEPLOY_ENV cannot disconnect a real investor mid-session.
//
// Behaviour (non-production only): after `idleMs` with no user activity in
// this tab, the socket is disconnected, automatic reconnection is switched
// off, and `onIdle` is called so the app can show a notice with a Reconnect
// control. `resume()` restores reconnection and reconnects.

import { isProductionBuild } from "@/utils/deployEnvironment";

export const DEFAULT_IDLE_MS = 30 * 60 * 1000;
export const DEFAULT_CHECK_MS = 60 * 1000;

const ACTIVITY_EVENTS = [
  "mousemove",
  "mousedown",
  "keydown",
  "touchstart",
  "scroll",
  "visibilitychange",
];

const NOOP_HANDLE = Object.freeze({
  resume() {},
  dispose() {},
  isIdleDisconnected() {
    return false;
  },
});

export function installSocketIdleGuard(socket, options = {}) {
  // `windowRef` is destructured before `isProduction` so the environment
  // resolver inspects the same window the guard attaches its listeners to,
  // rather than a global that may not be the one under test.
  const {
    idleMs = DEFAULT_IDLE_MS,
    checkMs = DEFAULT_CHECK_MS,
    windowRef = typeof window === "undefined" ? null : window,
    documentRef = typeof document === "undefined" ? null : document,
    isProduction = isProductionBuild({ windowRef }),
    onIdle = () => {},
    now = () => Date.now(),
  } = options;

  if (isProduction || !socket || !windowRef) {
    return NOOP_HANDLE;
  }

  let lastActivity = now();
  let idleDisconnected = false;
  let previousReconnection = null;
  let wasConnected = false;

  const markActivity = () => {
    if (documentRef && documentRef.visibilityState === "hidden") {
      return;
    }
    lastActivity = now();
  };

  ACTIVITY_EVENTS.forEach((eventName) => {
    windowRef.addEventListener(eventName, markActivity, { passive: true });
  });

  const check = () => {
    if (idleDisconnected) {
      return;
    }
    if (now() - lastActivity < idleMs) {
      return;
    }
    idleDisconnected = true;
    wasConnected = Boolean(socket.connected);
    if (socket.io && socket.io.opts) {
      previousReconnection = socket.io.opts.reconnection;
      socket.io.opts.reconnection = false;
    }
    socket.disconnect();
    onIdle({ idleMs, wasConnected });
  };

  const timer = setInterval(check, checkMs);

  const resume = () => {
    if (!idleDisconnected) {
      return;
    }
    idleDisconnected = false;
    lastActivity = now();
    if (socket.io && socket.io.opts && previousReconnection !== null) {
      socket.io.opts.reconnection = previousReconnection;
      previousReconnection = null;
    }
    if (wasConnected) {
      socket.connect();
    }
  };

  const dispose = () => {
    clearInterval(timer);
    ACTIVITY_EVENTS.forEach((eventName) => {
      windowRef.removeEventListener(eventName, markActivity);
    });
  };

  return {
    resume,
    dispose,
    isIdleDisconnected: () => idleDisconnected,
  };
}
